import {
  add,
  addMinutes,
  addMonths,
  type Duration,
  format,
  formatDistanceToNow,
  formatDistanceToNowStrict,
  isToday,
} from 'date-fns';

type FormatDistanceUnit = NonNullable<
  Parameters<typeof formatDistanceToNowStrict>[1]
>['unit'];

export type DateValue = Date | string | number | null | undefined;

export const DATE_FORMAT_MMM_DD_YYYY = 'MMM dd, yyyy';
export const DATE_FORMAT_DD_MMM_YYYY = 'dd MMM yyyy';
export const DATE_FORMAT_MMM_DD_HH_mm = 'MMM dd, HH:mm';
export const DATE_FORMAT_MMM_DD_YYYY_HH_mm = 'MMM dd, yyyy-HH:mm';
export const DATE_FORMAT_YYYY_MM_DD_HH_mm = 'yyyy-MM-dd HH:mm';
export const DATE_FORMAT_YYYY_MM_DD_hh_mm_a = 'yyyy-MM-dd hh:mm a';
export const DATE_FORMAT_MMM_YYY = 'MMM yyyy';
export const DATE_FORMAT_MM_DD_YYYY_hh_mm_a = 'MMM d, yyyy hh:mm a';

export function newDate(date: DateValue) {
  if (date) date = toMilisecond(date);
  return date ? new Date(date) : undefined;
}

export function newDateFormat(date: DateValue, fm?: string) {
  return date ? format(newDate(date)!, fm || 'basic') : '';
}

export function toMilisecond(date: DateValue) {
  if (typeof date === 'number' && date.toString().length === 10)
    return date * 1000;
  return date;
}

export function fDate(date: DateValue, newFormat?: string) {
  return newDateFormat(date, newFormat || DATE_FORMAT_MMM_DD_YYYY);
}

export function fDateShort(date: DateValue, newFormat?: string) {
  return newDateFormat(date, newFormat || DATE_FORMAT_MMM_DD_HH_mm);
}

export function fDateTime(date: DateValue, newFormat?: string) {
  return newDateFormat(date, newFormat || DATE_FORMAT_YYYY_MM_DD_HH_mm);
}

export function fDateTimeName(date: DateValue, newFormat?: string) {
  return newDateFormat(date, newFormat || 'dd MMM yyyy  HH:mm');
}

export function fTimestamp(date: DateValue) {
  return date ? toMilisecond(date) : '';
}

export function fToNow(
  date: DateValue,
  { strict = true, unit }: { strict?: boolean; unit?: FormatDistanceUnit } = {}
) {
  if (!date) return '';
  if (unit === 'day' && isToday(newDate(date)!)) {
    return 'today';
  }
  const formatter = strict ? formatDistanceToNowStrict : formatDistanceToNow;
  return formatter(newDate(date)!, { addSuffix: true, unit: unit });
}

export function fDateAdd(date: Date | string | number, duration: Duration) {
  try {
    return add(typeof date === 'string' ? Date.parse(date) : date, duration);
  } catch {
    return null;
  }
}

export function dateCompare(
  date1: DateValue,
  date2: DateValue,
  order: 'asc' | 'desc'
) {
  try {
    return order === 'desc'
      ? newDate(date2)!.getTime() - newDate(date1)!.getTime()
      : newDate(date1)!.getTime() - newDate(date2)!.getTime();
  } catch {
    return 0;
  }
}

export function todayTimestamp() {
  const d = new Date();
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  return d.getTime();
}

export function addMonthsDstSafe(date: Date, amount: number) {
  const endDate = addMonths(date, amount);
  return addMinutes(
    endDate,
    date.getTimezoneOffset() - endDate.getTimezoneOffset()
  );
}
